import React from 'react';
import { FileSearch, Palette, Code, Rocket, TrendingUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../i18n';

export const Process = () => {
  const { copy } = useLanguage();
  const icons = [FileSearch, Palette, Code, Rocket, TrendingUp];
  const colors = ['orange', 'red', 'yellow', 'green', 'blue'];
  const steps = copy.process.steps.map(([title, description, duration], index) => ({
    icon: icons[index],
    title,
    description,
    duration,
    color: colors[index],
  }));

  const colorClasses = {
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800'
  };

  return (
    <section id="process" className="py-24 bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-medium inline-block">
            {copy.process.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold">
            {copy.process.title}<span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">{copy.process.accent}</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {copy.process.intro}
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-500 border-2 overflow-hidden"
              >
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    <div className={`md:w-1/4 p-8 flex flex-col items-center justify-center text-center ${colorClasses[step.color]} border-b md:border-b-0 md:border-r-2`}>
                      <div className="mb-4 p-4 rounded-full bg-white/50 dark:bg-black/20 group-hover:scale-110 transition-transform">
                        <Icon className="h-12 w-12" />
                      </div>
                      <div className="text-5xl font-bold mb-2">{String(index + 1).padStart(2, '0')}</div>
                      <div className="text-sm font-medium opacity-75">{step.duration}</div>
                    </div>
                    
                    <div className="md:w-3/4 p-8 space-y-4">
                      <h3 className="text-2xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-2 border-orange-200 dark:border-orange-800">
          <div className="text-center space-y-4">
            <h3 className="text-2xl font-bold">{copy.process.methodology}</h3>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              {copy.process.methodologyText}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
